import React, { Component } from 'react';
import { Draggable } from 'react-beautiful-dnd';

import { ButtonIcon } from 'components/button';
import { Checkbox } from 'components/input/checkbox';
import { I18n, translate } from 'components/translate';
import { QuizService } from 'modules/content/quiz';
import PropTypes from 'prop-types';

import DroppableComponent from './DroppableComponent';

import { QuizEditorComponent } from '../editor';

const QUESTION_CHART_LIMIT = 500;

@translate()
export default class DraggableQuizComponent extends Component {
  static propTypes = {
    item: PropTypes.object,
    index: PropTypes.number,
    itemsQuantity: PropTypes.number,
    lastItem: PropTypes.bool,
    t: PropTypes.func,
    focusQuestion: PropTypes.func,
    addQuestion: PropTypes.func,
    type: PropTypes.string,
    questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  };

  constructor(props) {
    super(props);
    this.state = {
      item: this.props.item,
      itemsQuantity: this.props.itemsQuantity,
      charCount: 0
    };
  }

  getSnapshotBeforeUpdate() {
    if (JSON.stringify(this.state.item) !== JSON.stringify(this.props.item)) {
      return { item: this.props.item };
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (snapshot !== null) {
      this.setState(snapshot);
    }
  }

  updateCharCount = (count) => {
    this.setState({ charCount: count });
  };

  toggleCorrectAnswer(e, answerIndex, questionId) {
    const { checked } = e.target;

    if (!checked) {
      return false;
    } else {
      QuizService.changeCorrectAnswer(answerIndex, questionId);
    }
  }

  toggleRequiredQuestion(e, index) {
    QuizService.toggleRequiredQuestion(index, e.target.checked);
  }

  get createActions() {
    const { item, charCount } = this.state;
    const { type, index, questionId } = this.props;

    if (type.indexOf('answer') >= 0 && !QuizService.isProfile()) {
      return (
        <div className="bottom-actions">
          <Checkbox
            checked={item.correct}
            onChange={(e) => this.toggleCorrectAnswer(e, index, questionId)}
            className={`correct-answer-${questionId}`}
          >
            <I18n label="quiz_editor.correct_answer" />
          </Checkbox>
          {type !== 'question' && (
            <div className="char__limit">
              {charCount}{' '}
              <span>
                <I18n label="general.characters" />
              </span>
            </div>
          )}
          <div className="actions-wrapper">
            <ButtonIcon
              className="btn-quiz-action"
              transparent
              type="secondary"
              icon="delete"
              onClick={(e) => this.removeAnswer(e, index, questionId)}
              disabled={
                this.props.itemsQuantity <= 1 || QuizService.isProfile()
              }
            >
              <I18n label="general.remove" />
            </ButtonIcon>
          </div>
        </div>
      );
    } else if (type.indexOf('question') >= 0) {
      return (
        <div className="bottom-actions bottom-actions-card">
          <Checkbox
            checked={item.mandatory}
            onChange={(e) => this.toggleRequiredQuestion(e, index)}
            className="required-question"
          >
            <I18n label="general.mandatory" />
          </Checkbox>
          <div className="actions-wrapper">
            <ButtonIcon
              className="btn-quiz-action"
              transparent
              type="secondary"
              icon="duplicate"
              onClick={(e) => this.duplicateQuestion(e)}
            >
              <I18n label="general.duplicate" />
            </ButtonIcon>
            <ButtonIcon
              className="btn-quiz-action"
              transparent
              type="secondary"
              icon="delete"
              onClick={(e) => this.removeQuestion(e)}
            >
              <I18n label="general.remove" />
            </ButtonIcon>
          </div>
        </div>
      );
    }
  }

  removeAnswer = (e, answerIndex, questionId) => {
    e.preventDefault();
    e.stopPropagation();
    QuizService.removeAnswer(answerIndex, questionId);
  };

  removeQuestion = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { item } = this.state;

    QuizService.removeQuestion(item.id);
  };

  duplicateQuestion = () => {
    const { item } = this.state;
    const createdQuestion = QuizService.duplicateQuestion(item.id);

    this.props.focusQuestion(createdQuestion);
  };

  renderAnswers = (answers, questionId) => {
    return (
      <DroppableComponent
        questionId={questionId}
        componentFormat="quiz"
        key={`answer-${questionId}`}
        type={`answer-${questionId}`}
        items={answers}
        droppableId={`answer-droppable-${questionId}`}
      />
    );
  };

  render() {
    const { index, type, questionId } = this.props;
    const { item, charCount } = this.state;

    let customClassName = '';
    let placeholder = '';

    if (type === 'question') {
      customClassName = `question-${item.id} question-editor`;
      placeholder = this.props.t('quiz_editor.new_question');
    } else {
      customClassName = `question-${item.id} answer-${item.id} answer-editor`;
      placeholder = this.props.t('quiz_editor.new_answer');
    }

    return (
      <Draggable
        key={`${type}-${item.id}`}
        type={type}
        draggableId={`${type}-${item.id}`}
        id={index}
        index={index}
        onDragStart={(e) => this.onDragStart(e)}
      >
        {(provided) => (
          <div className={`draggable-${type}`}>
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              className={`rc-quiz-container ${type}`}
            >
              <span {...provided.dragHandleProps} className="drag-handler">
                <span></span>
                <span></span>
                <span></span>
              </span>
              <div className="rc-quiz-content-wrapper">
                <li className={`rc-quiz-content ${type}`} translate="no">
                  <QuizEditorComponent
                    index={this.props.index}
                    charCount={charCount}
                    updateCharCount={this.updateCharCount}
                    charLimit={
                      type !== 'question' ? QUESTION_CHART_LIMIT : null
                    }
                    placeholder={placeholder}
                    content={item.text}
                    className={customClassName}
                    type={type}
                    parentId={questionId}
                    id={item.id}
                  />
                  {item && item.answers && item.answers.length
                    ? this.renderAnswers(item.answers, item.id)
                    : null}
                </li>
                {this.createActions}
              </div>
            </div>
            {provided.placeholder}
            {type === 'question' && (
              <button
                className="button btn-add-hovering button__icon__left button__secondary button__transparent"
                onClick={(e) => {
                  this.props.addQuestion(e, index);
                }}
              >
                <span>
                  <i className="icon icon-plus"></i>
                  <div className="text">
                    <I18n label="quiz_editor.add_question" />
                  </div>
                </span>
              </button>
            )}
          </div>
        )}
      </Draggable>
    );
  }
}
